import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import AppButton from '../../../../components/designe/app-button/AppButton';
import { useAccess } from '../../../../hooks/useAccess';
import { useGetLanguages } from '../../../../services/redux/api/languagesAPI';
import {
  useCreateNotificationCampaignMutation,
  useGetNotificationCampaignByIdQuery,
  useGetNotificationTemplatesQuery,
  usePatchNotificationCampaignMutation,
} from '../../../../services/redux/api/notificationsAdminApi';
import {
  NOTIFICATION_DISPLAY_DOMAINS,
  type NotificationCampaignKind,
  type NotificationCampaignStatus,
  type NotificationDisplayDomain,
  type NotificationPreferenceGroup,
  type NotificationTemplateCategory,
} from '../../../../types/interfaces/notification-admin';
import {
  isCategoryPreferenceConsistent,
  preferenceGroupsForCategory,
} from '../../../../utils/notification-admin-rules';
import {
  notificationCategoryLabel,
  notificationDisplayDomainLabel,
  notificationPreferenceGroupLabel,
} from '../../../../utils/notification-template-labels';
import { notificationsBasePath } from '../notificationsBasePath';

const STATUSES: NotificationCampaignStatus[] = [
  'draft',
  'scheduled',
  'processing',
  'completed',
  'cancelled',
  'failed',
];

function isoUtcToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseOptionalJsonRecord(
  raw: string,
): Record<string, unknown> | null | 'invalid' {
  const t = raw.trim();
  if (t === '' || t === '{}') return null;
  try {
    const v = JSON.parse(t);
    if (v && typeof v === 'object' && !Array.isArray(v))
      return v as Record<string, unknown>;
  } catch {
    return 'invalid';
  }
  return 'invalid';
}

export function NotificationCampaignForm() {
  const { t } = useTranslation();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const isNew = !campaignId;
  const { access } = useAccess();
  const canMutate =
    (access?.notifications?.canCreate || access?.notifications?.canEdit) ??
    false;

  const { data: templates = [] } = useGetNotificationTemplatesQuery();
  const { data: languages = [] } = useGetLanguages();
  const { data: existing, isLoading: loadingC } =
    useGetNotificationCampaignByIdQuery(campaignId as string, {
      skip: isNew || !campaignId,
    });

  const [createCampaign, { isLoading: creating }] =
    useCreateNotificationCampaignMutation();
  const [patchCampaign, { isLoading: patching }] =
    usePatchNotificationCampaignMutation();

  const templateOptions = useMemo(
    () => templates.filter((x) => x.active),
    [templates],
  );

  const [campaignKind, setCampaignKind] =
    useState<NotificationCampaignKind>('template');
  const [templateCode, setTemplateCode] = useState('');
  const [templateVarsJson, setTemplateVarsJson] = useState('{}');
  const [languageId, setLanguageId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [status, setStatus] = useState<NotificationCampaignStatus>('draft');

  const [inlineTitle, setInlineTitle] = useState('');
  const [inlineBody, setInlineBody] = useState('');
  const [inlineCategory, setInlineCategory] =
    useState<NotificationTemplateCategory>('marketing');
  const [inlinePreference, setInlinePreference] =
    useState<NotificationPreferenceGroup>('marketing');
  const [inlineDomain, setInlineDomain] =
    useState<NotificationDisplayDomain>('marketing');

  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    const k: NotificationCampaignKind = existing.kind ?? 'template';
    setCampaignKind(k);
    setTemplateCode(existing.templateCode ?? '');
    setTemplateVarsJson(
      existing.templateVariablesJson &&
        typeof existing.templateVariablesJson === 'object'
        ? JSON.stringify(existing.templateVariablesJson, null, 2)
        : '{}',
    );
    const lid =
      existing.filterJson &&
      typeof existing.filterJson === 'object' &&
      typeof (existing.filterJson as { languageId?: unknown }).languageId ===
        'string'
        ? ((existing.filterJson as { languageId: string }).languageId as string)
        : '';
    setLanguageId(lid);
    setScheduledAt(
      existing.scheduledAt ? isoUtcToDatetimeLocal(existing.scheduledAt) : '',
    );
    setStatus(existing.status);
    const im = existing.inlineMessagesJson;
    if (im?.default) {
      setInlineTitle(im.default.title);
      setInlineBody(im.default.body);
    } else if (im?.byLanguageId && Object.keys(im.byLanguageId).length > 0) {
      const first = Object.values(im.byLanguageId)[0];
      setInlineTitle(first.title);
      setInlineBody(first.body);
    } else {
      setInlineTitle('');
      setInlineBody('');
    }
    if (existing.inlineMetaJson) {
      setInlineCategory(existing.inlineMetaJson.category);
      setInlinePreference(existing.inlineMetaJson.preferenceGroup);
      setInlineDomain(existing.inlineMetaJson.displayDomain);
    } else {
      setInlineCategory('marketing');
      setInlinePreference('marketing');
      setInlineDomain('marketing');
    }
  }, [existing]);

  useEffect(() => {
    if (!isNew || campaignKind !== 'template' || templateCode) return;
    const first = templateOptions[0];
    if (first) setTemplateCode(first.code);
  }, [isNew, campaignKind, templateCode, templateOptions]);

  const onInlineCategoryChange = (c: NotificationTemplateCategory) => {
    setInlineCategory(c);
    const allowed = preferenceGroupsForCategory(c);
    if (c === 'marketing') {
      setInlinePreference('marketing');
    } else if (!allowed.includes(inlinePreference)) {
      setInlinePreference(allowed[0]);
    }
  };

  const scheduledIso =
    scheduledAt.trim() === ''
      ? null
      : (() => {
          const d = new Date(scheduledAt);
          return Number.isNaN(d.getTime()) ? null : d.toISOString();
        })();

  const resolvedInlinePreference: NotificationPreferenceGroup =
    inlineCategory === 'marketing' ? 'marketing' : inlinePreference;

  const save = async () => {
    setSaveError(null);
    if (!languageId.trim()) {
      setSaveError(
        t('adminDashboard.notifications.campaigns.languageRequiredError'),
      );
      return;
    }
    const filterJson = { languageId };

    if (campaignKind === 'template') {
      if (!templateCode.trim()) return;
      const tv = parseOptionalJsonRecord(templateVarsJson);
      if (tv === 'invalid') {
        setSaveError(
          t('adminDashboard.notifications.campaigns.templateVariablesInvalid'),
        );
        return;
      }
      if (isNew) {
        const res = await createCampaign({
          templateCode: templateCode.trim(),
          templateVariablesJson: tv,
          filterJson,
          scheduledAt: scheduledIso,
          status,
        }).unwrap();
        const newId =
          res && typeof res === 'object' && 'id' in res ? res.id : undefined;
        if (typeof newId === 'string') {
          navigate(`${notificationsBasePath}/campaigns/${newId}`, {
            replace: true,
          });
        } else {
          navigate(`${notificationsBasePath}/campaigns`);
        }
      } else if (campaignId) {
        await patchCampaign({
          id: campaignId,
          body: {
            templateCode: templateCode.trim(),
            templateVariablesJson: tv,
            filterJson,
            scheduledAt: scheduledIso,
            status,
          },
        }).unwrap();
        navigate(`${notificationsBasePath}/campaigns`);
      }
      return;
    }

    if (!inlineTitle.trim() || !inlineBody.trim()) {
      setSaveError(
        t('adminDashboard.notifications.campaigns.inlineCopyRequired'),
      );
      return;
    }
    if (
      !isCategoryPreferenceConsistent(inlineCategory, resolvedInlinePreference)
    ) {
      setSaveError(
        t('adminDashboard.notifications.campaigns.inlineMetaInvalid'),
      );
      return;
    }

    if (isNew) {
      const res = await createCampaign({
        kind: 'inline',
        inlineMessagesJson: {
          default: {
            title: inlineTitle.trim(),
            body: inlineBody.trim(),
          },
        },
        inlineMetaJson: {
          category: inlineCategory,
          preferenceGroup: resolvedInlinePreference,
          displayDomain: inlineDomain,
        },
        filterJson,
        scheduledAt: scheduledIso,
        status,
      }).unwrap();
      const newId =
        res && typeof res === 'object' && 'id' in res ? res.id : undefined;
      if (typeof newId === 'string') {
        navigate(`${notificationsBasePath}/campaigns/${newId}`, {
          replace: true,
        });
      } else {
        navigate(`${notificationsBasePath}/campaigns`);
      }
    } else if (campaignId && existing) {
      const prevIm = existing.inlineMessagesJson;
      await patchCampaign({
        id: campaignId,
        body: {
          inlineMessagesJson: {
            ...(prevIm && typeof prevIm === 'object' ? prevIm : {}),
            default: {
              title: inlineTitle.trim(),
              body: inlineBody.trim(),
            },
          },
          inlineMetaJson: {
            category: inlineCategory,
            preferenceGroup: resolvedInlinePreference,
            displayDomain: inlineDomain,
          },
          filterJson,
          scheduledAt: scheduledIso,
          status,
        },
      }).unwrap();
      navigate(`${notificationsBasePath}/campaigns`);
    }
  };

  const saveDisabled =
    campaignKind === 'template'
      ? !templateCode.trim() || templateOptions.length === 0
      : !inlineTitle.trim() || !inlineBody.trim();

  if (!isNew && loadingC) {
    return <Typography>{t('adminDashboard.notifications.loading')}</Typography>;
  }

  if (!isNew && !existing) {
    return (
      <Typography>{t('adminDashboard.notifications.notFound')}</Typography>
    );
  }

  return (
    <Box maxWidth={560}>
      <AppButton
        variant="outlined"
        onClick={() => navigate(`${notificationsBasePath}/campaigns`)}
        sx={{ mb: 2 }}>
        {t('adminDashboard.notifications.backToList')}
      </AppButton>

      <Typography variant="h6" gutterBottom>
        {isNew
          ? t('adminDashboard.notifications.campaigns.createTitle')
          : t('adminDashboard.notifications.campaigns.editTitle')}
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        {t('adminDashboard.notifications.campaigns.filterHint')}
      </Alert>

      {!isNew && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('adminDashboard.notifications.campaigns.kindLabel')}:{' '}
          <strong>
            {campaignKind === 'template'
              ? t('adminDashboard.notifications.campaigns.kindTemplate')
              : t('adminDashboard.notifications.campaigns.kindInline')}
          </strong>
        </Typography>
      )}

      {isNew && (
        <FormControl component="fieldset" sx={{ mb: 2 }} disabled={!canMutate}>
          <Typography variant="subtitle2" gutterBottom>
            {t('adminDashboard.notifications.campaigns.campaignKind')}
          </Typography>
          <RadioGroup
            row
            value={campaignKind}
            onChange={(e) =>
              setCampaignKind(e.target.value as NotificationCampaignKind)
            }>
            <FormControlLabel
              value="template"
              control={<Radio />}
              label={t('adminDashboard.notifications.campaigns.kindTemplate')}
            />
            <FormControlLabel
              value="inline"
              control={<Radio />}
              label={t('adminDashboard.notifications.campaigns.kindInline')}
            />
          </RadioGroup>
        </FormControl>
      )}

      {campaignKind === 'template' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="ctpl">
              {t('adminDashboard.notifications.campaigns.templateCode')}
            </InputLabel>
            <Select
              labelId="ctpl"
              label={t('adminDashboard.notifications.campaigns.templateCode')}
              value={templateCode}
              onChange={(e) => setTemplateCode(e.target.value as string)}>
              {templateOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  {t('adminDashboard.notifications.campaigns.noTemplates')}
                </MenuItem>
              ) : (
                templateOptions.map((tpl) => (
                  <MenuItem key={tpl.id} value={tpl.code}>
                    {tpl.code} ({notificationCategoryLabel(t, tpl.category)})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label={t(
              'adminDashboard.notifications.campaigns.templateVariablesJson',
            )}
            value={templateVarsJson}
            onChange={(e) => setTemplateVarsJson(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            disabled={!canMutate}
            sx={{ mb: 2 }}
            helperText={t(
              'adminDashboard.notifications.campaigns.templateVariablesHint',
            )}
          />
        </>
      )}

      {campaignKind === 'inline' && (
        <>
          <TextField
            label={t('common.title')}
            value={inlineTitle}
            onChange={(e) => setInlineTitle(e.target.value)}
            fullWidth
            disabled={!canMutate}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('common.description')}
            value={inlineBody}
            onChange={(e) => setInlineBody(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            disabled={!canMutate}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="inl-cat">
              {t('adminDashboard.notifications.templates.category')}
            </InputLabel>
            <Select
              labelId="inl-cat"
              label={t('adminDashboard.notifications.templates.category')}
              value={inlineCategory}
              onChange={(e) =>
                onInlineCategoryChange(
                  e.target.value as NotificationTemplateCategory,
                )
              }>
              {(['marketing', 'transactional'] as const).map((c) => (
                <MenuItem key={c} value={c}>
                  {notificationCategoryLabel(t, c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="inl-pref">
              {t('adminDashboard.notifications.templates.preferenceGroup')}
            </InputLabel>
            <Select
              labelId="inl-pref"
              label={t(
                'adminDashboard.notifications.templates.preferenceGroup',
              )}
              value={resolvedInlinePreference}
              onChange={(e) =>
                setInlinePreference(
                  e.target.value as NotificationPreferenceGroup,
                )
              }>
              {preferenceGroupsForCategory(inlineCategory).map((p) => (
                <MenuItem key={p} value={p}>
                  {notificationPreferenceGroupLabel(t, p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
            <InputLabel id="inl-dom">
              {t('adminDashboard.notifications.templates.displayDomain')}
            </InputLabel>
            <Select
              labelId="inl-dom"
              label={t('adminDashboard.notifications.templates.displayDomain')}
              value={inlineDomain}
              onChange={(e) =>
                setInlineDomain(e.target.value as NotificationDisplayDomain)
              }>
              {NOTIFICATION_DISPLAY_DOMAINS.map((d) => (
                <MenuItem key={d} value={d}>
                  {notificationDisplayDomainLabel(t, d)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
        <InputLabel id="clang">
          {t('adminDashboard.notifications.campaigns.targetLanguage')}
        </InputLabel>
        <Select
          labelId="clang"
          label={t('adminDashboard.notifications.campaigns.targetLanguage')}
          value={languageId}
          onChange={(e) => setLanguageId(e.target.value as string)}
          required>
          {languages
            .filter((l) => l.id)
            .map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.title || l.key}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <TextField
        label={t('adminDashboard.notifications.campaigns.scheduledAtUtc')}
        value={scheduledAt}
        onChange={(e) => setScheduledAt(e.target.value)}
        fullWidth
        type="datetime-local"
        disabled={!canMutate}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
        helperText={t('adminDashboard.notifications.campaigns.scheduledAtHint')}
      />

      <FormControl fullWidth sx={{ mb: 2 }} disabled={!canMutate}>
        <InputLabel id="cst">
          {t('adminDashboard.notifications.campaigns.status')}
        </InputLabel>
        <Select
          labelId="cst"
          label={t('adminDashboard.notifications.campaigns.status')}
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as NotificationCampaignStatus)
          }>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {canMutate && (
        <AppButton
          onClick={save}
          loader={creating || patching}
          disabled={saveDisabled}>
          {t('common.save')}
        </AppButton>
      )}
    </Box>
  );
}
